import cx from 'classnames';
import Hammer from 'hammerjs';
import React, { PropsWithChildren, useEffect, useRef } from 'react';
import styles from './SwipeLeftToClose.mscss';

interface OwnProps {
    className?: string;
    onClose();
}

type Props = OwnProps;

const SwipeLeftToClose: React.FunctionComponent<Props> = ({ className, children, onClose }) => {
    const containerRef = useRef<HTMLDivElement>();
    const contentRef = useRef<HTMLDivElement>();

    useEffect(() => {
        const hammer = new Hammer(containerRef.current, { inputClass: Hammer.TouchInput });
        hammer.get('pan').set({ direction: Hammer.DIRECTION_HORIZONTAL });

        let panStartX;
        let panStartTime;
        let animationDispose: () => void;

        const velocity = 0.3;

        const addAnimation = () => {
            if (animationDispose) {
                animationDispose();
            }

            contentRef.current.classList.add(styles['SwipeLeftToClose--animating']);

            const remove = () => {
                contentRef.current.classList.remove(styles['SwipeLeftToClose--animating']);
                if (animationDispose) {
                    animationDispose();
                }
            };

            animationDispose = () => {
                contentRef.current.removeEventListener('transitionend', remove, false);
                animationDispose = null;
            };

            contentRef.current.addEventListener('transitionend', remove, false);
        };

        const onPanStart = (event: HammerInput) => {
            event.preventDefault();
            panStartX = event.deltaX;
            panStartTime = Date.now();
        };

        const onPan = (event: HammerInput) => {
            event.preventDefault();
            const distance = panStartX - event.deltaX;

            // we are swiping left
            if (distance > 0) {
                contentRef.current.style.transform = `translate3d(-${distance}px, 0, 0)`;
            }
        };

        const onPanEnd = (event: HammerInput) => {
            event.preventDefault();
            const distance = panStartX - event.deltaX;
            const contentWidth = contentRef.current.offsetWidth;
            if (
                distance > contentWidth * 0.5 || // We swipe past 50%
                distance / (Date.now() - panStartTime) > velocity // Fast swipe
            ) {
                onClose();
            } else {
                addAnimation();
                contentRef.current.style.transform = `translate3d(0, 0, 0)`;
            }
        };

        hammer.on('pan', onPan);
        hammer.on('panstart', onPanStart);
        hammer.on('panend', onPanEnd);

        return () => {
            if (animationDispose) {
                animationDispose();
            }

            hammer.off('pan', onPan);
            hammer.off('panstart', onPanStart);
            hammer.off('panend', onPanEnd);

            hammer.stop(true);
            hammer.destroy();
        };
    }, [contentRef, onClose]);

    return (
        <div className={styles.SwipeLeftToClose} ref={containerRef}>
            <div className={className} ref={contentRef}>
                {children}
            </div>
        </div>
    );
};

SwipeLeftToClose.displayName = 'SwipeLeftToClose';

export default React.memo<PropsWithChildren<Props>>(SwipeLeftToClose);
